import type { SampleScore } from '../types/samples';

const MAX_VALUE_CHARS = 40;

// C0 controls (ESC, CR, LF, BEL, TAB...), DEL, and the C1 range. Mirrors
// _CONTROL_CHARS in hawk/cli/list.py, where these are a terminal injection
// vector. React escapes HTML so the viewer is not at risk; the substitution is
// here so both surfaces render one identical string.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;

/** Replace every control character with U+FFFD, as the CLI does. */
function neutralizeControls(text: string): string {
  return text.replace(CONTROL_CHARS, '�');
}

/** Cap a rendered value at MAX_VALUE_CHARS, ellipsis included in the cap. */
function clipValue(rendered: string): string {
  // Code points, not UTF-16 code units: Python's `_clip` measures code points,
  // so `.length` here would clip an astral character (emoji, CJK extension B...)
  // that the CLI leaves whole, and `.slice` could cut a surrogate pair in half
  // and emit a lone high surrogate before the ellipsis.
  const chars = Array.from(rendered);
  return chars.length > MAX_VALUE_CHARS
    ? chars.slice(0, MAX_VALUE_CHARS - 1).join('') + '…'
    : rendered;
}

/**
 * Render one score's value, clipping a long string/object only when `clip` is
 * set. Control characters are neutralised either way. Marks intermediates.
 */
function renderScoreValue(score: SampleScore, clip: boolean): string {
  const rendered = renderScoreBody(score, clip);
  // Outside the clip, matching _format_sample_score in hawk/cli/list.py.
  return score.is_intermediate ? `${rendered} (intermediate)` : rendered;
}

function renderScoreBody(score: SampleScore, clip: boolean): string {
  // value_nonfinite leads: a NaN score is a marker, not a blank.
  if (score.value_nonfinite !== null && score.value_nonfinite !== undefined) {
    return score.value_nonfinite;
  }

  const value = score.value;
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  if (typeof value === 'object') {
    // JSON.stringify only escapes C0 controls (CR, LF, tab...); C1 controls
    // (U+0080-U+009F) and DEL pass through verbatim, so neutralizeControls runs
    // afterward to catch those too. It's a no-op on the already-escaped C0
    // text, so both sides still render byte-identical.
    const rendered = neutralizeControls(JSON.stringify(value));
    return clip ? clipValue(rendered) : rendered;
  }
  const rendered = neutralizeControls(String(value));
  return clip ? clipValue(rendered) : rendered;
}

/** Render one score's value, clipped. */
export function formatScoreValue(score: SampleScore): string {
  return renderScoreValue(score, true);
}

/**
 * Render a scorer name for a grid cell.
 *
 * Mirrors _format_scorer_name in hawk/cli/list.py — keep the two in step.
 */
function formatScorerName(scorer: string): string {
  // A scorer name is whatever the task package registered with @scorer, so it
  // is eval-controlled data like a string score value. React escapes it here,
  // but the CLI prints it verbatim, and both surfaces must render one identical
  // string.
  return clipValue(neutralizeControls(scorer));
}

/**
 * Format scores for a grid cell.
 *
 * A single score renders bare (`0.71`); several are prefixed
 * (`bombe_scorer=0.71, match=C`) so unrelated metrics can't be confused.
 * Mirrors _format_scores_compact in hawk/cli/list.py — keep the two in step.
 */
export function formatScores(scores: SampleScore[]): string {
  if (!scores || scores.length === 0) return '-';
  if (scores.length === 1) return formatScoreValue(scores[0]);

  const parts = scores
    .slice(0, 3)
    .map(s => `${formatScorerName(s.scorer)}=${formatScoreValue(s)}`);
  return parts.join(', ') + (scores.length > 3 ? '...' : '');
}

/**
 * Format every score in full, for the grid cell's `title` tooltip.
 *
 * Caps nothing: every entry, always `scorer=value` prefixed, values unclipped,
 * so hovering a truncated cell reveals what it left out. Control characters are
 * still neutralised.
 */
export function formatScoresFull(scores: SampleScore[]): string {
  if (!scores || scores.length === 0) return '-';
  // Scorer names are neutralised but not clipped: revealing what the cell
  // truncated is this tooltip's whole purpose, so it caps neither half of the
  // pair.
  return scores
    .map(s => `${neutralizeControls(s.scorer)}=${renderScoreValue(s, false)}`)
    .join(', ');
}
